import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiEdit2, FiTrash2, FiUser, FiMapPin, FiPhone, FiX, FiImage, FiGlobe, FiHash, FiFlag, FiDollarSign, FiSearch } = FiIcons;

const CustomerManager = ({ onUpdate }) => {
    const [customers, setCustomers] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        email: '',
        vatNumber: '',
        website: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'Malaysia',
        group: '',
        currency: 'System Default',
        language: 'System Default',
        paymentTerm: 'Cash on Delivery',
        deliverySOP: '',
        locationLink: '',
        image: null
    });
    const [activeTab, setActiveTab] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');
    const paymentTerms = ['All', 'Cash on Delivery', '7 Days', '14 Days', '30 Days', '60 Days'];

    useEffect(() => {
        fetchCustomers();
    }, []);

    const fetchCustomers = async () => {
        try {
            const response = await fetch('/api/customers');
            if (response.ok) {
                const data = await response.json();
                setCustomers(data);
                if (onUpdate) onUpdate();
            }
        } catch (error) {
            console.error('Failed to fetch customers:', error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);

        try {
            const url = editingCustomer
                ? `/api/customers/${editingCustomer.id}`
                : '/api/customers';

            const method = editingCustomer ? 'PUT' : 'POST';

            const data = new FormData();
            // Append all string fields
            Object.keys(formData).forEach(key => {
                if (key !== 'image') {
                    data.append(key, formData[key] || '');
                }
            });

            // Append image if provided
            if (formData.image instanceof File) {
                data.append('image', formData.image);
            }

            const response = await fetch(url, {
                method,
                body: data,
            });

            if (response.ok) {
                await fetchCustomers();
                resetForm();
            }
        } catch (error) {
            console.error('Failed to save customer:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this customer?')) return;

        try {
            const response = await fetch(`/api/customers/${id}`, {
                method: 'DELETE',
            });

            if (response.ok) {
                fetchCustomers();
            }
        } catch (error) {
            console.error('Failed to delete customer:', error);
        }
    };

    const resetForm = () => {
        setIsModalOpen(false);
        setEditingCustomer(null);
        setFormData({
            name: '',
            contact: '',
            email: '',
            vatNumber: '',
            website: '',
            address: '',
            city: '',
            state: '',
            zipCode: '',
            country: 'Malaysia',
            group: '',
            currency: 'System Default',
            language: 'System Default',
            paymentTerm: 'Cash on Delivery',
            deliverySOP: '',
            locationLink: '',
            image: null
        });
    };

    const handleEdit = (customer) => {
        setEditingCustomer(customer);
        setFormData({
            name: customer.name,
            contact: customer.contact || '',
            email: customer.email || '',
            vatNumber: customer.vatNumber || '',
            website: customer.website || '',
            address: customer.address || '',
            city: customer.city || '',
            state: customer.state || '',
            zipCode: customer.zipCode || '',
            country: customer.country || 'Malaysia',
            group: customer.group || '',
            currency: customer.currency || 'System Default',
            language: customer.language || 'System Default',
            paymentTerm: customer.paymentTerm || 'Cash on Delivery',
            deliverySOP: customer.deliverySOP || '',
            locationLink: customer.locationLink || '',
            image: null
        });
        setIsModalOpen(true);
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setFormData(prev => ({ ...prev, image: e.target.files[0] }));
        }
    };

    return (
        <div className="p-6 md:p-10 max-w-7xl mx-auto pb-24 md:pb-8">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 md:mb-10 gap-4">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-[#1e3a8a] tracking-tight">Customers</h1>
                    <p className="text-gray-500 mt-2 font-light">Manage your client base</p>
                </div>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="w-full md:w-auto bg-[#16a34a] hover:bg-green-700 text-white px-6 py-3 rounded-lg flex items-center justify-center space-x-2 transition-all shadow-md shadow-green-100 font-medium"
                >
                    <SafeIcon icon={FiPlus} />
                    <span>New Customer</span>
                </button>
            </div>

            {/* Payment Term Tabs & Search */}
            <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                <div className="flex space-x-2 bg-white p-1 rounded-xl w-full md:w-fit border border-gray-100 shadow-sm overflow-x-auto no-scrollbar">
                    {paymentTerms.map(term => (
                        <button
                            key={term}
                            onClick={() => setActiveTab(term)}
                            className={`px-4 py-2 text-sm font-medium rounded-lg transition-all whitespace-nowrap flex-shrink-0 ${activeTab === term
                                ? 'bg-[#16a34a] text-white shadow-md shadow-green-100'
                                : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'
                                }`}
                        >
                            {term}
                        </button>
                    ))}
                </div>

                <div className="relative w-full md:w-64">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                        <SafeIcon icon={FiSearch} />
                    </span>
                    <input
                        type="text"
                        placeholder="Search customers..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-100 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] text-sm font-light shadow-sm"
                    />
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
                {customers
                    .filter(c => {
                        const matchesTerm = activeTab === 'All' || (c.paymentTerm || 'Cash on Delivery') === activeTab;
                        const matchesSearch = c.name.toLowerCase().includes(searchQuery.toLowerCase());
                        return matchesTerm && matchesSearch;
                    })
                    .map(customer => (
                        <div key={customer.id} className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 hover:shadow-lg transition-all duration-300">
                            <div className="flex items-start justify-between mb-6">
                                <div className="flex items-center space-x-4">
                                    <div className="w-32 h-24 bg-blue-50 rounded-lg flex items-center justify-center text-[#1e3a8a] shadow-inner overflow-hidden flex-shrink-0 border border-blue-100">
                                        {customer.image ? (
                                            <img src={customer.image} alt={customer.name} className="w-full h-full object-cover" />
                                        ) : (
                                            <SafeIcon icon={FiUser} className="text-4xl opacity-50" />
                                        )}
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-medium text-gray-900">{customer.name}</h3>
                                        {customer.city && <p className="text-xs text-gray-500">{customer.city}, {customer.country}</p>}
                                    </div>
                                </div>
                                <div className="flex space-x-1">
                                    <button onClick={() => handleEdit(customer)} className="p-2 text-gray-400 hover:text-[#1e3a8a] hover:bg-blue-50 rounded-lg transition-all"><SafeIcon icon={FiEdit2} /></button>
                                    <button onClick={() => handleDelete(customer.id)} className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all"><SafeIcon icon={FiTrash2} /></button>
                                </div>
                            </div>

                            <div className="space-y-3">
                                {customer.contact && (
                                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                                        <SafeIcon icon={FiPhone} className="text-gray-400" />
                                        <span>{customer.contact}</span>
                                    </div>
                                )}
                                {customer.address && (
                                    <div className="flex items-start space-x-3 text-sm text-gray-600">
                                        <SafeIcon icon={FiMapPin} className="text-gray-400 mt-1" />
                                        <span>
                                            {customer.address}
                                            {customer.city && <><br />{customer.city}</>}
                                            {customer.state && `, ${customer.state}`}
                                            {customer.zipCode && ` ${customer.zipCode}`}
                                        </span>
                                    </div>
                                )}
                                {customer.vatNumber && (
                                    <div className="flex items-center space-x-3 text-sm text-gray-600">
                                        <SafeIcon icon={FiHash} className="text-gray-400" />
                                        <span className="text-xs text-gray-500 uppercase tracking-widest">VAT: {customer.vatNumber}</span>
                                    </div>
                                )}
                                {!customer.contact && !customer.address && (
                                    <div className="text-sm text-gray-400 italic">No contact details provided</div>
                                )}
                                <div className="flex items-center space-x-3 text-sm text-gray-600">
                                    <SafeIcon icon={FiDollarSign} className="text-gray-400" />
                                    <span className="text-xs text-gray-500 uppercase tracking-widest">{customer.paymentTerm || 'COD'}</span>
                                </div>
                                {customer.locationLink && (
                                    <a href={customer.locationLink} target="_blank" rel="noopener noreferrer" className="flex items-center space-x-3 text-sm text-[#16a34a] hover:underline">
                                        <SafeIcon icon={FiMapPin} className="text-[#16a34a]" />
                                        <span>View Location</span>
                                    </a>
                                )}
                                {customer.deliverySOP && (
                                    <div className="mt-4 pt-3 border-t border-gray-50">
                                        <div className="flex items-center space-x-2 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
                                            <SafeIcon icon={FiFlag} />
                                            <span>Delivery SOP</span>
                                        </div>
                                        <p className="text-sm text-gray-600 leading-relaxed">{customer.deliverySOP}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
            </div>

            {isModalOpen && (
                <div className="fixed inset-0 bg-[#1e3a8a]/20 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl shadow-xl w-full max-w-4xl overflow-hidden max-h-[90vh] overflow-y-auto">
                        <div className="p-6 border-b border-gray-100 bg-gray-50 flex justify-between items-center sticky top-0 z-10">
                            <h2 className="text-xl font-light text-[#1e3a8a]">{editingCustomer ? 'Edit Customer' : 'New Customer'}</h2>
                            <button onClick={resetForm} className="text-gray-400 hover:text-gray-600"><SafeIcon icon={FiX} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="p-8 space-y-6">

                            {/* Image Upload Area */}
                            <div className="flex justify-center mb-8">
                                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center border-2 border-dashed border-gray-300 relative overflow-hidden group hover:border-[#16a34a] transition-all">
                                    {formData.image instanceof File ? (
                                        <img src={URL.createObjectURL(formData.image)} alt="Preview" className="w-full h-full object-cover" />
                                    ) : editingCustomer && editingCustomer.image ? (
                                        <img src={editingCustomer.image} alt="Current" className="w-full h-full object-cover" />
                                    ) : (
                                        <SafeIcon icon={FiImage} className="text-gray-400 text-2xl group-hover:text-[#16a34a]" />
                                    )}
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                        className="absolute inset-0 opacity-0 cursor-pointer"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                {/* Left Column: Basic Info */}
                                <div className="space-y-6">
                                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Customer Details</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Company *</label>
                                        <input
                                            type="text"
                                            value={formData.name}
                                            onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. Skyline Hotel"
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">VAT Number</label>
                                        <input
                                            type="text"
                                            value={formData.vatNumber}
                                            onChange={(e) => setFormData(p => ({ ...p, vatNumber: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Phone</label>
                                        <input
                                            type="text"
                                            value={formData.contact}
                                            onChange={(e) => setFormData(p => ({ ...p, contact: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. 012-3456789"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Website</label>
                                        <input
                                            type="text"
                                            value={formData.website}
                                            onChange={(e) => setFormData(p => ({ ...p, website: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. www.example.com"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Groups</label>
                                        <select
                                            value={formData.group}
                                            onChange={(e) => setFormData(p => ({ ...p, group: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                                        >
                                            <option value="">Nothing selected</option>
                                            <option value="VIP">VIP</option>
                                            <option value="Wholesale">Wholesale</option>
                                            <option value="Retail">Retail</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
                                        <select
                                            value={formData.currency}
                                            onChange={(e) => setFormData(p => ({ ...p, currency: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                                        >
                                            <option value="System Default">System Default</option>
                                            <option value="MYR">MYR</option>
                                            <option value="USD">USD</option>
                                            <option value="SGD">SGD</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Default Language</label>
                                        <select
                                            value={formData.language}
                                            onChange={(e) => setFormData(p => ({ ...p, language: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                                        >
                                            <option value="System Default">System Default</option>
                                            <option value="English">English</option>
                                            <option value="Malay">Malay</option>
                                            <option value="Chinese">Chinese</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Payment Term</label>
                                        <select
                                            value={formData.paymentTerm}
                                            onChange={(e) => setFormData(p => ({ ...p, paymentTerm: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                                        >
                                            <option value="Cash on Delivery">Cash on Delivery</option>
                                            <option value="7 Days">7 Days</option>
                                            <option value="14 Days">14 Days</option>
                                            <option value="30 Days">30 Days</option>
                                            <option value="60 Days">60 Days</option>
                                        </select>
                                    </div>

                                </div>

                                {/* Right Column: Address */}
                                <div className="space-y-6">
                                    <h3 className="text-sm font-semibold text-gray-900 border-b pb-2">Billing Address</h3>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                                        <textarea
                                            value={formData.address}
                                            onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light resize-none h-32"
                                            placeholder="Street address..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                        <input
                                            type="text"
                                            value={formData.city}
                                            onChange={(e) => setFormData(p => ({ ...p, city: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. Johor Bahru"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                        <input
                                            type="text"
                                            value={formData.state}
                                            onChange={(e) => setFormData(p => ({ ...p, state: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. Johor"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Zip Code</label>
                                        <input
                                            type="text"
                                            value={formData.zipCode}
                                            onChange={(e) => setFormData(p => ({ ...p, zipCode: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="e.g. 81100"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Country</label>
                                        <select
                                            value={formData.country}
                                            onChange={(e) => setFormData(p => ({ ...p, country: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light bg-white"
                                        >
                                            <option value="Malaysia">Malaysia</option>
                                            <option value="Singapore">Singapore</option>
                                            <option value="Thailand">Thailand</option>
                                            <option value="Indonesia">Indonesia</option>
                                        </select>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Location Link</label>
                                        <input
                                            type="text"
                                            value={formData.locationLink}
                                            onChange={(e) => setFormData(p => ({ ...p, locationLink: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light"
                                            placeholder="https://maps.google.com/..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Delivery SOP</label>
                                        <textarea
                                            value={formData.deliverySOP}
                                            onChange={(e) => setFormData(p => ({ ...p, deliverySOP: e.target.value }))}
                                            className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#16a34a]/20 focus:border-[#16a34a] outline-none transition-all font-light resize-none h-24"
                                            placeholder="Specific delivery instructions..."
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex space-x-3 pt-4 border-t border-gray-100 mt-8">
                                <button type="submit" disabled={isLoading} className="flex-1 bg-[#16a34a] text-white py-4 rounded-lg font-medium hover:bg-green-700 transition-all shadow-lg shadow-green-100 disabled:opacity-50">
                                    {isLoading ? 'Saving...' : 'Save Customer'}
                                </button>
                                <button type="button" onClick={resetForm} className="px-6 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition-all">
                                    Cancel
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CustomerManager;
